import { store } from '../store';
import { wsPostLiked, wsPostUnliked, wsNewComment, wsPostEdited, wsNotification } from '../store/slices/postsSlice';
import { wsNewMessage, wsMessagesRead } from '../store/slices/messagesSlice';

type EventHandler = (data: any) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners: Map<string, Set<EventHandler>> = new Map();
  private intentionalClose = false;

  connect() {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    this.intentionalClose = false;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${window.location.host}/api/v1/ws?token=${token}`;

    try {
      this.ws = new WebSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.handleMessage(data);
      } catch (err) {
        console.error('WebSocket message parse error:', err);
      }
    };

    this.ws.onclose = () => {
      console.log('WebSocket disconnected');
      if (!this.intentionalClose) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // onclose will fire after onerror
    };
  }

  disconnect() {
    this.intentionalClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.reconnectAttempts = 0;
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) return;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts++;
    console.log(`WebSocket reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private handleMessage(data: any) {
    const currentUserId = store.getState().auth.user?.id;

    switch (data.type) {
      case 'post_liked':
        // Skip if this user did the like (already updated optimistically)
        if (data.user_id !== currentUserId) {
          store.dispatch(wsPostLiked(data));
        }
        break;
      case 'post_unliked':
        if (data.user_id !== currentUserId) {
          store.dispatch(wsPostUnliked(data));
        }
        break;
      case 'new_comment':
        store.dispatch(wsNewComment(data));
        break;
      case 'post_edited':
        store.dispatch(wsPostEdited(data));
        break;
      case 'notification':
        store.dispatch(wsNotification(data));
        break;
      case 'new_message':
        store.dispatch(wsNewMessage(data));
        break;
      case 'messages_read':
        store.dispatch(wsMessagesRead(data));
        break;
    }

    // Emit to component-level listeners
    const handlers = this.listeners.get(data.type);
    if (handlers) {
      handlers.forEach((handler) => handler(data));
    }
  }

  on(event: string, handler: EventHandler) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  off(event: string, handler: EventHandler) {
    this.listeners.get(event)?.delete(handler);
  }
}

export const wsService = new WebSocketService();
